<script setup lang="ts">
  import { ref, nextTick } from 'vue';
  import type { OrderResource, OrderItem, ProductForReview } from '@storefront/resources';

  interface Props {
    item: OrderItem,
    order: OrderResource
  }

  defineProps<Props>();

  const product = ref<ProductForReview>();
  const url = ref<string | null>(null);
  const open = ref(false);

  function sendReview(item: OrderItem) {
    product.value = {
      id: item.variantId,
      // because of v-if="item.reviewUrl && item.variantId"
      // we know variant for this item exists
      photo: item.photo!,
      title: item.variantTitle!,
      sku: item.variantSku!,
    };

    url.value = item.reviewUrl;

    nextTick(() => {
      open.value = true;
    });
  }
</script>
<template>
  <SfListCard class="line" large>
    <template #image>
      <MediaPicture :photo="item.photo" sizes="140x140 2x, 70x70" />
    </template>
    <template #default>
      <div class="line__content">
        <h2 class="line__heading" v-html="item.variantTitle" />
        <p v-if="item.variantSku" class="line__sku" v-html="$t('SHOP.ORDER.LABEL.SKU', { code: item.variantSku })" />
      </div>
    </template>
    <template #icons>
      <div v-if="item.regularGross && item.regularGross !== item.priceGross" class="line__discount">
        {{ $t('SHOP.ORDER.LABEL.DISCOUNT') }}
        <SfFormattedPercentage :value="((Number(item.regularGross) - Number(item.priceGross)) / Number(item.regularGross)) * 100" :negative="false" />
      </div>
    </template>
    <template #actions>
      <div class="line__pricing">
        <div v-if="item.regularGross && item.regularGross !== item.priceGross" class="line__regular">
          <SfMoney :code="order.currencyCode" :decimals="order.currency.decimals" :value="item.regularGross" />
        </div>
        <div class="line__price">
          <SfUnit :unit="item.stockUnit" :quantity="item.amount" />
          &times;
          <SfMoney :code="order.currencyCode" :decimals="order.currency.decimals" :value="item.priceGross" />
        </div>
      </div>
      <SfButton v-if="item.reviewUrl && item.variantId" primary small @click="sendReview(item)">
        {{ $t('SHOP.ORDER.ACTION.REVIEW_ORDER_ITEM') }}
      </SfButton>
    </template>
  </SfListCard>
  <ReviewFormModal
    v-if="product"
    :variant="product"
    :url="url"
    v-model:open="open"
  />
</template>
<style scoped lang="scss">
  @use "@storefront/styles/mixins/media";

  .line {
    &__heading {
      font-weight: var(--font-weight-bold);
      font-size: 1rem;
      word-break: break-all;
    }

    &__discount {
      font-weight: var(--font-weight-bold);
      font-size: 0.875rem;
    }

    &__sku {
      font-size: 0.75rem;
      color: var(--secondary-color);
    }

    &__regular {
      color: var(--danger-color);
      font-size: 0.875rem;
      text-decoration: line-through;
    }

    &__price {
      white-space: nowrap;
    }

    :deep(.list-card__image) {
      flex: 0 0 70px;

      @include media.query(md) {
        flex: 0 0 45px;
      }

      picture, img {
        display: flex;
        aspect-ratio: 1/1;
      }
    }
  }
</style>
